import { useEffect, useLayoutEffect, useRef, useState } from 'react'

export const HOME_THEME_STORAGE_KEY = 'wholesale-home-theme'
const isTheme = (value) => value === 'light' || value === 'dark'

// Own document styles at the route boundary, even while an application page is loading.
export const useHomeDocument = (enabled) => {
  useLayoutEffect(() => {
    const root = document.documentElement
    root.classList.toggle('homeDocument', enabled)
    document.body.classList.toggle('homePage', enabled)
    if (!enabled) delete root.dataset.homeTheme
    return () => {
      root.classList.remove('homeDocument')
      document.body.classList.remove('homePage')
      delete root.dataset.homeTheme
    }
  }, [enabled])
}

const readPreference = () => {
  try {
    const value = window.localStorage.getItem(HOME_THEME_STORAGE_KEY)
    return isTheme(value) ? value : null
  } catch (_) {
    return null
  }
}

export const useHomeTheme = (enabled) => {
  // CSS and the prepaint bootstrap handle the initial appearance, including server rendering.
  const [theme, setTheme] = useState('light')
  const preference = useRef(null)

  useEffect(() => {
    if (!enabled) {
      delete document.documentElement.dataset.homeTheme
      return undefined
    }
    let media
    try { media = window.matchMedia?.('(prefers-color-scheme: dark)') } catch (_) {}
    preference.current = readPreference() || preference.current
    const apply = (value) => {
      document.documentElement.dataset.homeTheme = value
      setTheme(value)
    }
    apply(preference.current || (media?.matches ? 'dark' : 'light'))
    const followSystem = (event) => {
      if (!preference.current) apply(event.matches ? 'dark' : 'light')
    }
    if (media?.addEventListener) media.addEventListener('change', followSystem)
    else media?.addListener?.(followSystem)
    return () => {
      if (media?.removeEventListener) media.removeEventListener('change', followSystem)
      else media?.removeListener?.(followSystem)
      delete document.documentElement.dataset.homeTheme
    }
  }, [enabled])

  const toggleTheme = () => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark'
    preference.current = nextTheme
    document.documentElement.dataset.homeTheme = nextTheme
    setTheme(nextTheme)
    try { window.localStorage.setItem(HOME_THEME_STORAGE_KEY, nextTheme) } catch (_) {}
  }

  return [theme, toggleTheme]
}
